declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace NodeJS {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface ProcessEnv {
      // NODE_ENV はビルド時の最適化に利用されるため、静的解析が効くように process.env のままにする
      readonly NODE_ENV: "development" | "production" | "test";
      // NEXT_RUNTIME を参照したい場面では外部パッケージがうまく動かない可能性があるため、process.env から参照する
      readonly NEXT_RUNTIME?: "nodejs" | "edge";
      /**
       * The Environment that the app is deployed and running on.
       * @see https://vercel.com/docs/projects/environment-variables/system-environment-variables
       */
      readonly VERCEL_ENV?: "production" | "preview" | "development";
    }
  }
}

export const appEnvs = ["dev", "feat", "integ", "prd"] as const;
export type AppEnv = (typeof appEnvs)[number];

/**
 * APP_ENV を Datadog の env tag 値に変換する。社内の環境区分ルール
 * (Integration Preview / Feature Preview) に揃えて、Logs / RUM / Vercel
 * native log の env が一致するようにする。
 *
 * Vercel Log Drain native ログの `@environment` 属性は仕様上
 * `production` / `preview` / `development` の 3 値しか取らないため、
 * `integration` / `feature` への分岐は Datadog Log Pipeline で
 * `@branch` / `@environment` から復元する。
 */
export const APP_ENV_TO_DATADOG_ENV: Record<AppEnv, string> = {
  prd: "production",
  integ: "integration",
  feat: "feature",
  dev: "development",
};

export const defaultLocaleString = "ja";
export const supportedLocales = [
  defaultLocaleString,
  "en",
  "zh-CN",
  "zh-TW",
] as const;
export type LocaleString = (typeof supportedLocales)[number];

/**
 * Key は BCP 47 (`LocaleString`)。
 */
export type LocalizedString = Partial<Record<LocaleString, string>>;

/**
 * グローバルなGraphQLエラー
 * 操作固有でなく横断的に処理する種別で、GraphQL Error の extensions.code に使用する
 * https://www.notion.so/sanu/API-376d14f248a88032b6a8efc672164c46
 */
export const GlobalGraphQLErrors = {
  // https://netflix.github.io/dgs/error-handling/#the-errortype-enumeration
  BAD_REQUEST: "BAD_REQUEST",
  FAILED_PRECONDITION: "FAILED_PRECONDITION",
  INTERNAL: "INTERNAL",
  NOT_FOUND: "NOT_FOUND",
  PERMISSION_DENIED: "PERMISSION_DENIED",
  UNAUTHENTICATED: "UNAUTHENTICATED",
  UNAVAILABLE: "UNAVAILABLE",
  UNKNOWN: "UNKNOWN",
} as const;
export type GlobalGraphQLErrorType =
  (typeof GlobalGraphQLErrors)[keyof typeof GlobalGraphQLErrors];

/**
 * 上記 ErrorType では粒度が荒くクライアントで分岐できないケースに使う sub Type。
 * 親 ErrorType ごとにグループ化して定義する。
 */
export const GlobalGraphQLErrorSubTypes = {
  [GlobalGraphQLErrors.UNAUTHENTICATED]: {
    // 未認証メール競合時のエラー
    UNVERIFIED_EMAIL_CONFLICT: "UNVERIFIED_EMAIL_CONFLICT",
    // ソーシャルプロバイダーの Auth0 ユーザーID で直接ユーザー作成しようとした場合のエラー
    // post-login action でのアカウントリンクがスキップまたは失敗した場合に発生する
    SOCIAL_IDENTITY_NOT_LINKED: "SOCIAL_IDENTITY_NOT_LINKED",
    // 退会済みユーザーのセッションが残存している場合のエラー。クライアントは強制ログアウトする
    DELETED_USER_SESSION: "DELETED_USER_SESSION",
  },
} as const satisfies Partial<
  Record<GlobalGraphQLErrorType, Readonly<Record<string, string>>>
>;
type SubTypesOf<T> = { [P in keyof T]: T[P][keyof T[P]] }[keyof T];

export type GlobalGraphQLErrorSubType = SubTypesOf<
  typeof GlobalGraphQLErrorSubTypes
>;

/** 特定の親 ErrorType に属する sub type だけを取り出す。 */
export type GlobalGraphQLErrorSubTypeFor<T extends GlobalGraphQLErrorType> =
  T extends keyof typeof GlobalGraphQLErrorSubTypes
    ? SubTypesOf<Pick<typeof GlobalGraphQLErrorSubTypes, T>>
    : never;
